import type { ChangeEventHandler, FC, MouseEventHandler } from 'react';
import { memo, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Image, Text } from '@theme-ui/components';

import FormattedMoney from '@app/components/FormattedMoney';
import { isSoftwareCartItem } from '@app/helpers/shop';
import type { CartQuery_magento2_cart_items } from '@app/pages/Cart/__generated__/LegacyCartQueriesTypes';
import { Magento2_ProductStockStatus } from '@__generated__/globalTypes';

import { CartGridCell } from '../../Cart.styles';
import messages from './CartItem.messages';
import { QuantitySelect, RemoveButton } from './CartItem.styles';

export const DEFAULT_CART_ITEM_MAX_QUANTITY = 5;
export const GIFT_CARD_ITEM_MAX_QUANTITY = 2;

type CartItemProps = {
  item: CartQuery_magento2_cart_items;
  includingTax: boolean;
  onQuantitySelect: (id: string, quantity: number) => void;
  onRemove: (id: string) => void;
};

const CartItem: FC<CartItemProps> = ({
  item,
  onQuantitySelect,
  includingTax,
  onRemove,
}) => {
  const { quantity, uid: id, product, prices } = item;

  /*
    Matches current shop logic - if more than 5 in cart, allow up to current, otherwise cap at 5
    See: https://bitbucket.bln.native-instruments.de/projects/webdev/repos/website-integration/browse/src%2Fmagento%2Fapp%2Fdesign%2Ffrontend%2Fpro%2Fni%2Ftemplate%2Fcheckout%2Fcart%2Fitem%2Fdefault.phtml?at=77afb2d259a6082d376e072b88d9b45610f0b6a3#116
  */

  const maxQuantity: number =
    item.__typename === 'Magento2_GiftCardCartItem'
      ? GIFT_CARD_ITEM_MAX_QUANTITY
      : DEFAULT_CART_ITEM_MAX_QUANTITY;

  const quantityOptions = useMemo(
    () =>
      Array.from(
        { length: Math.max(maxQuantity, quantity || 0) },
        (_, i) => i + 1,
      ),
    [maxQuantity, quantity],
  );

  const handleQuantitySelect: ChangeEventHandler<HTMLSelectElement> = (
    event,
  ) => {
    if (!id) return;

    const nextQuantity = Number(event.currentTarget.value);

    if (Number.isFinite(nextQuantity)) onQuantitySelect(id, nextQuantity);
  };

  const handleRemove: MouseEventHandler<HTMLButtonElement> = () => {
    if (id) onRemove(id);
  };

  const isOutOfStock =
    product.stockStatus === Magento2_ProductStockStatus.OUT_OF_STOCK;

  return (
    <>
      <CartGridCell
        hAlign="center"
        data-testid={`item-${item.product.sku}-image`}
      >
        <Image
          sx={{ maxWidth: '100%' }}
          src={product.smallImage?.url || ''}
          alt={product.name || ''}
        />
      </CartGridCell>
      <CartGridCell>
        <Text
          as="p"
          sx={{ fontWeight: 500 }}
          mb={12}
          data-testid={`item-${item.product.sku}-title`}
        >
          {product.name}
          {isSoftwareCartItem(item) && (
            <>
              {' ('}
              <span data-testid={`item-${item.product.sku}-download`}>
                <FormattedMessage {...messages.itemDownloadableIndicator} />
              </span>
              {')'}
            </>
          )}
        </Text>
        <RemoveButton
          data-testid={`item-${item.product.sku}-remove`}
          type="transparent"
          onClick={handleRemove}
        >
          <FormattedMessage {...messages.itemRemove} />
        </RemoveButton>
      </CartGridCell>
      <CartGridCell>
        <span data-testid={`item-${item.product.sku}-price`}>
          {includingTax
            ? prices?.priceIncludingTax && (
                <FormattedMoney {...prices.priceIncludingTax} />
              )
            : prices?.price && <FormattedMoney {...prices.price} />}
        </span>
      </CartGridCell>
      <CartGridCell>
        <QuantitySelect
          name="quantity"
          onChange={handleQuantitySelect}
          value={quantity}
          isValid={!isOutOfStock}
          data-testid={`item-${item.product.sku}-quantity`}
        >
          {quantityOptions.map((option) => (
            <option value={option} key={option}>
              {option}
            </option>
          ))}
        </QuantitySelect>
      </CartGridCell>
      <CartGridCell hAlign="right">
        <span data-testid={`item-${item.product.sku}-total-price`}>
          {includingTax
            ? prices?.rowTotalIncludingTax && (
                <FormattedMoney {...prices.rowTotalIncludingTax} />
              )
            : prices?.rowTotal && <FormattedMoney {...prices.rowTotal} />}
        </span>
      </CartGridCell>
    </>
  );
};

export default memo(CartItem);
